import React, { useEffect, useState } from 'react';
import { Animated, StyleProp, View, ViewStyle } from 'react-native';
import { Icon, IconName } from '@/components/Icon';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { useSidebarAnimation } from '@/components/ui/therapist/CollapsibleSidebar/SidebarAnimationContext';
import { SelectedNavItem } from '@/components/ui/therapist/CollapsibleSidebar/index';
import { Text } from '@/components/Text';
import { useOnHover } from '@/hooks/useOnHover';

import { styles } from './styles';
import colors from '@/config/colors';

interface MenuItemProps {
  icon: IconName;
  label: string;
  labelOpacity: Animated.AnimatedInterpolation<string | number>;
  labelTranslateX: Animated.AnimatedInterpolation<string | number>;
  onPress: () => void;
  outerStyle?: StyleProp<ViewStyle>;
  screensActive?: string[];
  showChevron?: boolean;
  selectedNavItem?: SelectedNavItem;
}

export const MenuItem = ({
  icon,
  label,
  labelOpacity,
  labelTranslateX,
  onPress,
  outerStyle,
  screensActive,
  showChevron,
  selectedNavItem,
}: MenuItemProps) => {
  const [localIsSelected, setLocalIsSelected] = useState<boolean>(false);
  const { appTitle } = useSidebarAnimation();
  const { isHovered, handleHoverIn, handleHoverOut } = useOnHover();

  useEffect(() => {
    setLocalIsSelected(
      screensActive ? screensActive.includes(appTitle) : false,
    );
  }, [appTitle]);

  useEffect(() => {
    setLocalIsSelected(selectedNavItem === label);
  }, [selectedNavItem]);

  useEffect(() => {
    const isRouteActive = screensActive
      ? screensActive.includes(appTitle)
      : false;
    const isExpanded = selectedNavItem === label;

    setLocalIsSelected(isExpanded || isRouteActive);
  }, [appTitle, selectedNavItem]);

  return (
    <PressableNeolexon
      onPress={onPress}
      onHoverIn={handleHoverIn}
      onHoverOut={handleHoverOut}
      style={outerStyle}
    >
      <View
        style={[
          styles.menuItemContainer,
          localIsSelected && {
            backgroundColor: colors.newUI.therapist.bgBlue10,
            borderLeftWidth: 8,
            borderColor: colors.newUI.therapist.green70,
            paddingLeft: 0,
          },
          isHovered && { opacity: 0.5 },
        ]}
      >
        <Icon
          name={icon as IconName}
          size={28}
          color={colors.newUI.therapist.primaryBlue80}
          style={{ paddingLeft: 4 }}
        />
        <Animated.View
          style={[
            styles.expandedMenuItemWrapper,
            {
              opacity: labelOpacity,
              transform: [{ translateX: labelTranslateX }],
            },
          ]}
        >
          <Text style={{ paddingLeft: 15, fontWeight: 600 }}>{label}</Text>
          {showChevron && (
            <Icon
              name={'chevronRight'}
              size={20}
              color={colors.newUI.therapist.primaryBlue80}
              style={{ paddingRight: 40 }}
            />
          )}
        </Animated.View>
      </View>
    </PressableNeolexon>
  );
};
